import React from 'react';
import { Zap, Shield, BookOpen, Heart } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const About = () => {
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'All calculations happen in your browser. No server roundtrips, no delays. Just instant results.',
      color: 'text-saffron-600'
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Your data never leaves your device. We don\'t track you, store your calculations, or sell your information.',
      color: 'text-indgreen-600'
    },
    {
      icon: BookOpen,
      title: 'Learn & Calculate',
      description: 'Every calculation comes with formulas, explanations, and Indian context. Understand the math behind results.',
      color: 'text-saffron-600'
    },
    {
      icon: Heart,
      title: 'Made with ❤️ for Bharat',
      description: 'Built by Indians, for Indians. With Indian currency, tax slabs, measurements, and financial products in mind.',
      color: 'text-indgreen-600'
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-b from-saffron-50 via-white to-white border-b border-slate-100 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge variant="outline" className="mb-4 bg-white/80 border-saffron-200 text-saffron-700 px-3 py-1">
            About Us
          </Badge>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-bharat-navy leading-tight">
            Every calculation you need,
            <span className="block mt-2">
              <span className="text-saffron-500">made</span>{' '}
              <span className="text-indgreen-600">for Bharat.</span>
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
            bharat-calculator.com is a free, privacy-focused online calculator platform designed for India. 
            We believe every Indian deserves access to simple, accurate, and trustworthy calculation tools.
          </p>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Our Mission */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-bharat-navy mb-6">Our Mission</h2>
          <div className="bg-gradient-to-r from-saffron-50 to-indgreen-50 rounded-xl border border-slate-200 p-8">
            <p className="text-lg text-slate-700 leading-relaxed">
              We're on a mission to provide the best, most accurate, and privacy-respecting calculators for everyday 
              needs. From financial planning with Indian rupees (₹) to health metrics, scientific calculations, and 
              more — we've got you covered. No ads that distract. No tracking. No data sales. Just pure utility.
            </p>
          </div>
        </section>

        {/* Why We Built This */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-bharat-navy mb-6">Why We Built This</h2>
          <p className="text-slate-700 mb-4">
            Most online calculators are:
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <span className="text-saffron-600 font-bold">✕</span>
              <span className="text-slate-700"><strong>Cluttered with ads</strong> — making them slow and distracting</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-saffron-600 font-bold">✕</span>
              <span className="text-slate-700"><strong>Not India-focused</strong> — missing Indian currency, tax systems, and financial products</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-saffron-600 font-bold">✕</span>
              <span className="text-slate-700"><strong>Privacy-invasive</strong> — tracking users and selling data</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-saffron-600 font-bold">✕</span>
              <span className="text-slate-700"><strong>Outdated</strong> — using old technology and poor UI/UX</span>
            </li>
          </ul>
          <p className="text-slate-700">
            We're changing that. bharat-calculator.com is built modern, clean, and with privacy as a core principle.
          </p>
        </section>

        {/* Features Grid */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-bharat-navy mb-8">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-saffron-300 transition-colors">
                  <div className={`w-12 h-12 rounded-lg ${feature.color === 'text-saffron-600' ? 'bg-saffron-100' : 'bg-indgreen-100'} flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-bharat-navy mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* By The Numbers */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-bharat-navy mb-8">By The Numbers</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { number: '32+', label: 'Calculators' },
              { number: '50K+', label: 'Daily Users' },
              { number: '100%', label: 'Free Forever' },
              { number: '0', label: 'Ads (Zero!)' },
            ].map((stat, idx) => (
              <div key={idx} className="bg-gradient-to-br from-saffron-50 to-indgreen-50 rounded-lg border border-slate-200 p-6 text-center">
                <div className="font-display text-3xl font-bold text-bharat-navy">{stat.number}</div>
                <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* What's Next */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-bharat-navy mb-6">What's Next</h2>
          <p className="text-slate-700 mb-4">
            We're continuously adding new calculators and features based on user feedback:
          </p>
          <ul className="space-y-2">
            <li className="text-slate-700">✓ More financial tools specific to Indian investing</li>
            <li className="text-slate-700">✓ Cryptocurrency and forex calculators</li>
            <li className="text-slate-700">✓ Advanced statistical calculators</li>
            <li className="text-slate-700">✓ Mobile app (iOS & Android)</li>
            <li className="text-slate-700">✓ Multi-language support</li>
          </ul>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-bharat-navy to-slate-800 rounded-2xl p-8 md:p-10 text-white text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Got feedback or ideas?</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            We'd love to hear from you. Your suggestions help us build better calculators for everyone.
          </p>
          <a 
            href="/contact" 
            className="inline-block bg-saffron-500 hover:bg-saffron-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Get In Touch →
          </a>
        </section>
      </div>
    </div>
  );
};

export default About;

